import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Crown, ExternalLink, Wand2, RefreshCw, AlertCircle, CheckCircle2, CircleDashed } from "lucide-react";

/**
 * Admin → Founder NFTs tab
 *
 * Shows every Founding Ambassador in serial order with their on-chain
 * NFT mint status. Lets the admin:
 *   • Mint a single NFT (Wand button on each row)
 *   • Batch-mint all unminted ones (top-right "Mint All Pending")
 *   • Open each NFT on the XRPL Explorer (token ID link)
 *
 * The mint flow is server-driven: the row goes from "not_minted" →
 * "submitted" → "minted" (or "needs_lookup" / "failed"). We refresh the
 * list after every action so the admin can see progress in real time.
 */
export default function AdminFounderNFTs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState({}); // map userId → true while minting
  const [batchRunning, setBatchRunning] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/founder-nft/list");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load founder NFTs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const mintOne = async (userId) => {
    setMinting((m) => ({ ...m, [userId]: true }));
    try {
      const { data } = await api.post("/admin/founder-nft/mint", { userId });
      toast.success(`Minted NFT for serial #${data.foundingNumber}`);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mint failed");
    } finally {
      setMinting((m) => ({ ...m, [userId]: false }));
    }
  };

  const mintBatch = async () => {
    if (!window.confirm("Mint Founder NFTs on the XRP Ledger for ALL pending Founding Ambassadors? This is a real on-chain transaction.")) return;
    setBatchRunning(true);
    try {
      const { data } = await api.post("/admin/founder-nft/mint-batch", {});
      toast.success(`Batch complete — minted: ${data.minted} · skipped: ${data.skipped} · failed: ${data.failed}`);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch mint failed");
    } finally {
      setBatchRunning(false);
    }
  };

  const explorerUrl = (txOrToken, isToken = false) => {
    if (!txOrToken) return null;
    // XRPL Explorer URL — same domain for mainnet & testnet
    const base = isToken
      ? `https://livenet.xrpl.org/nft/${txOrToken}`
      : `https://livenet.xrpl.org/transactions/${txOrToken}`;
    return base;
  };

  const minted = rows.filter((r) => r.status === "minted").length;
  const pending = rows.filter((r) => r.status === "not_minted").length;
  const failed = rows.filter((r) => r.status === "failed").length;

  return (
    <div className="space-y-6" data-testid="admin-founder-nfts">
      <div>
        <div className="label-mystic">XRPL · Phase 3.5</div>
        <h2 className="font-serif text-3xl mt-1">Founder NFTs</h2>
        <p className="text-foreground/60 text-sm mt-2 max-w-2xl">
          Mint commemorative on-chain NFTs for the first <b>100 Founding Ambassadors</b>.
          Each NFT carries a unique serial number, is transferable (so the
          holder can keep it across wallets), and lives forever on the XRP Ledger.
          If the ambassador has a connected Xaman wallet we also create a
          0-XRP sell-offer destined to them — they tap &quot;Claim&quot; in Xaman to take ownership.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total founding" value={rows.length} testid="kpi-total" />
        <Kpi label="Minted on-chain" value={minted} accent="emerald" testid="kpi-minted" />
        <Kpi label="Pending mint" value={pending} accent="amber" testid="kpi-pending" />
        <Kpi label="Failed" value={failed} accent="rose" testid="kpi-failed" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={mintBatch}
          disabled={batchRunning || pending === 0}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded bg-primary text-primary-foreground uppercase tracking-[0.2em] text-xs disabled:opacity-50"
          data-testid="founder-nft-batch-mint"
        >
          <Wand2 size={14} className={batchRunning ? "animate-pulse" : ""} />
          {batchRunning ? "Minting…" : `Mint All Pending (${pending})`}
        </button>
        <button
          onClick={fetchRows}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-secondary/30 hover:bg-secondary/10 text-xs uppercase tracking-[0.22em] disabled:opacity-50"
          data-testid="founder-nft-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-md border border-secondary/15 overflow-x-auto" data-testid="founder-nft-table">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/[0.04] text-foreground/70 text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Ambassador</th>
              <th className="text-left px-4 py-3">XRPL Wallet</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">On-chain</th>
              <th className="text-left px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/60">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/60" data-testid="founder-nft-empty">
                No Founding Ambassadors yet — they&apos;ll appear here once approved with founding status.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.userId} className="border-t border-secondary/10 hover:bg-secondary/[0.03]">
                <td className="px-4 py-3 font-serif text-2xl text-secondary">#{r.foundingNumber}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-foreground/50 text-xs">{r.email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {r.xrplAddress ? (
                    <span className="text-secondary">{r.xrplAddress.slice(0, 8)}…{r.xrplAddress.slice(-6)}</span>
                  ) : (
                    <span className="text-foreground/40">no wallet</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={r.status} />
                  {r.error && (
                    <div className="text-rose-400 text-xs mt-1 max-w-[200px]" title={r.error}>
                      {r.error.slice(0, 60)}…
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.nftokenId && (
                    <a href={explorerUrl(r.nftokenId, true)} target="_blank" rel="noreferrer"
                       className="text-secondary inline-flex items-center gap-1 hover:underline">
                      Token <ExternalLink size={10} />
                    </a>
                  )}
                  {r.mintTxHash && (
                    <div>
                      <a href={explorerUrl(r.mintTxHash)} target="_blank" rel="noreferrer"
                         className="text-secondary inline-flex items-center gap-1 hover:underline">
                        Mint tx <ExternalLink size={10} />
                      </a>
                    </div>
                  )}
                  {r.offerTxHash && (
                    <div>
                      <a href={explorerUrl(r.offerTxHash)} target="_blank" rel="noreferrer"
                         className="text-secondary inline-flex items-center gap-1 hover:underline">
                        Claim offer <ExternalLink size={10} />
                      </a>
                      <span className="text-amber-400 ml-1 text-[10px] uppercase">{r.offerStatus}</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.status === "minted" ? (
                    <span className="text-foreground/40 text-xs">—</span>
                  ) : (
                    <button
                      onClick={() => mintOne(r.userId)}
                      disabled={!!minting[r.userId] || !r.approved}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-secondary/30 hover:bg-secondary/10 text-xs uppercase tracking-wider disabled:opacity-50"
                      data-testid={`mint-founder-${r.foundingNumber}`}
                    >
                      <Wand2 size={11} className={minting[r.userId] ? "animate-pulse" : ""} />
                      {minting[r.userId] ? "Minting…" : "Mint"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  const tone =
    accent === "emerald" ? "border-emerald-500/30 bg-emerald-500/[0.05]"
    : accent === "amber" ? "border-amber-500/30 bg-amber-500/[0.05]"
    : accent === "rose"  ? "border-rose-500/30 bg-rose-500/[0.05]"
    : "border-secondary/20 bg-secondary/[0.04]";
  return (
    <div className={`p-4 rounded-md border ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/60">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    minted:       { Icon: CheckCircle2,  tone: "bg-emerald-500/15 text-emerald-400" },
    not_minted:   { Icon: CircleDashed,  tone: "bg-foreground/10 text-foreground/60" },
    submitted:    { Icon: RefreshCw,     tone: "bg-amber-500/15 text-amber-400" },
    needs_lookup: { Icon: AlertCircle,   tone: "bg-amber-500/15 text-amber-400" },
    failed:       { Icon: AlertCircle,   tone: "bg-rose-500/15 text-rose-400" },
  }[status] || { Icon: Crown, tone: "bg-foreground/10 text-foreground/60" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${cfg.tone}`}>
      <cfg.Icon size={11} /> {status.replace(/_/g, " ")}
    </span>
  );
}
